import type { RouteObject } from "react-router";

/**
 * The Payroll workspace's routes (M26 W0-C-03, contracts §20), in a file of their own like `labourRoutes.tsx` —
 * `index.tsx` mounts this under the shell in one line.
 *
 * One layout route for the whole workspace: `PayrollWorkspaceGate` answers the add-on question once (the
 * entitlements, or any payroll read refused with `402 ADDON_REQUIRED` — "Payroll works while Staff is active" when
 * Staff is what is missing) and shows the way forward in the page's place. Every page is a file in its owning lane's
 * folder (`src/modules/payroll/<area>/pages/`); a lane replaces the file and never edits this table. Static segments
 * come before the parameter beside them (`employees/import` before `employees/:personId`). A `:month` is the
 * `YYYY-MM` label of a Staff period.
 */
export const payrollRoute: RouteObject = {
  path: "payroll",
  lazy: async () => ({
    Component: (await import("@/modules/payroll/shared/PayrollWorkspaceGate")).PayrollWorkspaceGate,
  }),
  children: [
    {
      // The runs list (C4): `/payroll` is the workspace's home.
      index: true,
      handle: { title: "Payroll runs" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/runs/pages/PayrollRunsPage")).PayrollRunsPage,
      }),
    },
    {
      path: "runs/:month",
      handle: { title: "Payroll run" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/runs/pages/PayrollRunPage")).PayrollRunPage,
      }),
    },
    {
      // A payslip explained, keyed on the month and the shared `people.id`; the run page's drawer shows the same.
      path: "runs/:month/payslips/:personId",
      handle: { title: "Payslip" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/runs/pages/PayrollPayslipPage")).PayrollPayslipPage,
      }),
    },
    {
      path: "employees",
      handle: { title: "Employees" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/employees/pages/PayrollEmployeesPage"))
          .PayrollEmployeesPage,
      }),
    },
    {
      // Static segments BEFORE `:personId`, so the router can never read the word "import" as a person.
      path: "employees/import",
      handle: { title: "Import salary structures" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/employees/pages/PayrollImportStructuresPage"))
          .PayrollImportStructuresPage,
      }),
    },
    {
      path: "employees/bulk",
      handle: { title: "Salary structures in bulk" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/employees/pages/PayrollBulkStructuresPage"))
          .PayrollBulkStructuresPage,
      }),
    },
    {
      // Keyed on the shared `people.id`: an employee IS a person (ADR-0017), so the core person page's
      // "Also in: Payroll" link (`personClaims`) lands here. `?tab=` picks a tab.
      path: "employees/:personId",
      handle: { title: "Employee pay" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/employees/pages/PayrollEmployeePage"))
          .PayrollEmployeePage,
      }),
    },
    {
      // `?month&person&kind` narrow the list.
      path: "inputs",
      handle: { title: "Inputs" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/inputs/pages/PayrollInputsPage")).PayrollInputsPage,
      }),
    },
    {
      // The declarations list, `?fy&status`.
      path: "tax",
      handle: { title: "Income tax" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/tax/pages/PayrollTaxPage")).PayrollTaxPage,
      }),
    },
    {
      path: "tax/declarations/:id",
      handle: { title: "Tax declaration" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/tax/pages/PayrollDeclarationPage"))
          .PayrollDeclarationPage,
      }),
    },
    {
      path: "tax/settings",
      handle: { title: "Tax settings" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/tax/pages/PayrollTaxSettingsPage"))
          .PayrollTaxSettingsPage,
      }),
    },
    {
      path: "tax/prior-income",
      handle: { title: "Prior income" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/tax/pages/PayrollPriorIncomePage"))
          .PayrollPriorIncomePage,
      }),
    },
    {
      path: "payments",
      handle: { title: "Payments" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/payments/pages/PayrollPaymentsPage")).PayrollPaymentsPage,
      }),
    },
    {
      path: "payments/:month",
      handle: { title: "Month's payments" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/payments/pages/PayrollPaymentsMonthPage"))
          .PayrollPaymentsMonthPage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum).
      path: "reports",
      handle: { title: "Payroll reports" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/reports/pages/PayrollReportsPage")).PayrollReportsPage,
      }),
    },
    {
      // `:kind` is the report's address segment (`PAYROLL_REPORT_IDS`: register, cost-summary, payment-status,
      // headcount-cost); its page chooses the report.
      path: "reports/:kind",
      handle: { title: "Payroll report" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/reports/pages/PayrollReportPage")).PayrollReportPage,
      }),
    },
    {
      // `?tab=` (`PAYROLL_SETTINGS_TABS`): general, payslip, components, templates, statutory.
      path: "settings",
      handle: { title: "Payroll settings" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/settings/pages/PayrollSettingsPage")).PayrollSettingsPage,
      }),
    },
    {
      // The setup guide (lane G): `?step=<id>`, `?person=<id>`. The breadcrumb's title is "Setup guide".
      path: "setup",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/payroll/setup/pages/PayrollSetupPage")).PayrollSetupPage,
      }),
    },
  ],
};
